import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigationMode } from './navigation-mode-context';
import { useUserAppsContext } from './user-apps-context';

const PHASE = {
    X_AXIS: 'X_AXIS',
    Y_AXIS: 'Y_AXIS',
};

// Standard web DPI conversion: 96 DPI = 96 pixels per inch
// 1 inch = 2.54 cm, so 1 cm ≈ 37.8 pixels
const CM_TO_PX = 96 / 2.54;

/**
 * X/Y Cursor Navigation Component
 * Renders animated cursors that allow users to select a point on screen
 * by sequentially choosing X and Y coordinates.
 * Cursor speed is in cm/second and is converted to pixels/second for animation.
 */
export default function XYCursorNavigation() {
    const { t } = useTranslation();
    const { navigationMode, xyCursorActive } = useNavigationMode();
    const { userSettings } = useUserAppsContext();
    const [phase, setPhase] = useState(PHASE.X_AXIS);
    const [cursorX, setCursorX] = useState(0);
    const [cursorY, setCursorY] = useState(0);
    const [fixedX, setFixedX] = useState(null);
    const [directionX, setDirectionX] = useState(1); // 1 = forward, -1 = backward
    const [directionY, setDirectionY] = useState(1);
    const animationFrameRef = useRef(null);
    const startTimeRef = useRef(null);
    const lastProgressRef = useRef(0);

    // Get cursor speed from settings (in seconds to traverse full screen)
    const cursorSpeed = userSettings?.cursor_speed || 3;

    // Trigger click at the intersection point
    const triggerClick = useCallback((x, y) => {
        const element = document.elementFromPoint(x, y);
        if (element) {
            // Dispatch the full mouse event sequence (mousedown → mouseup → click)
            // This ensures compatibility with UI components that listen for the complete sequence
            const eventOptions = {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: x,
                clientY: y,
            };

            const mousedownEvent = new MouseEvent('mousedown', eventOptions);
            element.dispatchEvent(mousedownEvent);

            const mouseupEvent = new MouseEvent('mouseup', eventOptions);
            element.dispatchEvent(mouseupEvent);

            const clickEvent = new MouseEvent('click', eventOptions);
            element.dispatchEvent(clickEvent);
        }

        // Reset to X axis and restart (keep current position and direction)
        setPhase(PHASE.X_AXIS);
        setFixedX(null);
        startTimeRef.current = null;
        lastProgressRef.current = 0;
        // Don't reset cursorX, cursorY, directionX, directionY - continue from current state
    }, []);

    // Handle Enter key press
    useEffect(() => {
        if (navigationMode !== 'xy_cursor' || !xyCursorActive) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();

                if (phase === PHASE.X_AXIS) {
                    // Fix X position and move to Y axis
                    setFixedX(cursorX);
                    setPhase(PHASE.Y_AXIS);
                    setCursorY(0);
                    startTimeRef.current = null;
                } else if (phase === PHASE.Y_AXIS) {
                    // Trigger click at (fixedX, cursorY)
                    triggerClick(fixedX, cursorY);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigationMode, xyCursorActive, phase, cursorX, cursorY, fixedX, triggerClick]);

    // Animate cursor movement with ping-pong (reverse at edges)
    useEffect(() => {
        if (navigationMode !== 'xy_cursor' || !xyCursorActive) return;

        // Reset start time when animation begins
        startTimeRef.current = null;

        const isXAxis = phase === PHASE.X_AXIS;
        const maxValue = isXAxis ? window.innerWidth : window.innerHeight;
        const speedInPixelsPerSecond = cursorSpeed * CM_TO_PX; // Convert cm/s to px/s
        const duration = (maxValue / speedInPixelsPerSecond) * 1000; // Time to traverse screen in ms
        const currentDirection = isXAxis ? directionX : directionY;
        const setDirection = isXAxis ? setDirectionX : setDirectionY;

        const animate = (currentTime) => {
            if (!startTimeRef.current) {
                startTimeRef.current = currentTime;
            }

            const elapsed = currentTime - startTimeRef.current;
            let progress = (elapsed / duration); // Linear progress 0 to 1+

            // Update progress based on direction and handle reversals
            if (currentDirection === 1) {
                // Moving forward (0 → 1)
                if (progress >= 1) {
                    // Reached end, reverse direction
                    setDirection(-1);
                    startTimeRef.current = currentTime;
                    lastProgressRef.current = 1;
                    progress = 1;
                }
            } else {
                // Moving backward (1 → 0)
                progress = lastProgressRef.current - progress;
                if (progress <= 0) {
                    // Reached start, reverse direction
                    setDirection(1);
                    startTimeRef.current = currentTime;
                    lastProgressRef.current = 0;
                    progress = 0;
                }
            }

            const position = progress * maxValue;

            if (isXAxis) {
                setCursorX(position);
            } else {
                setCursorY(position);
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [navigationMode, xyCursorActive, phase, cursorSpeed, directionX, directionY]);

    // Don't render if not in xy_cursor mode or not active
    if (navigationMode !== 'xy_cursor' || !xyCursorActive) {
        return null;
    }

    return (
        <>
            {/* Semi-transparent overlay */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    pointerEvents: 'none',
                    zIndex: 9998,
                }}
            />

            {/* X-axis cursor (vertical line) */}
            {phase === PHASE.X_AXIS && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: `${cursorX}px`,
                        width: '4px',
                        height: '100vh',
                        backgroundColor: 'secondary.main',
                        boxShadow: '0 0 8px rgba(0,0,0,0.3)',
                        pointerEvents: 'none',
                        zIndex: 9999,
                    }}
                />
            )}

            {/* Fixed X-axis cursor (when in Y phase) */}
            {phase === PHASE.Y_AXIS && fixedX !== null && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: `${fixedX}px`,
                        width: '4px',
                        height: '100vh',
                        backgroundColor: 'secondary.main',
                        opacity: 0.5,
                        pointerEvents: 'none',
                        zIndex: 9999,
                    }}
                />
            )}

            {/* Y-axis cursor (horizontal line) */}
            {phase === PHASE.Y_AXIS && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: `${cursorY}px`,
                        left: 0,
                        width: '100vw',
                        height: '4px',
                        backgroundColor: 'secondary.main',
                        boxShadow: '0 0 8px rgba(0,0,0,0.3)',
                        pointerEvents: 'none',
                        zIndex: 9999,
                    }}
                />
            )}

            {/* Intersection marker */}
            {phase === PHASE.Y_AXIS && fixedX !== null && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: `${cursorY - 8}px`,
                        left: `${fixedX - 8}px`,
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: 'secondary.main',
                        border: '2px solid white',
                        boxShadow: '0 0 8px rgba(0,0,0,0.5)',
                        pointerEvents: 'none',
                        zIndex: 10000,
                    }}
                />
            )}
        </>
    );
}
